import { FiFilter } from 'react-icons/fi'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'

import { LocalFilters } from '../types'

import { usePremioCategoria } from '@/hooks/API/premio/usePremioCategoria'

interface FiltrosAvancadosProps {
  isDrawerOpen: boolean
  setIsDrawerOpen: (open: boolean) => void
  localFilters: LocalFilters
  updateLocalFilter: (key: keyof LocalFilters, value: any) => void
  applyFilters: () => void
  clearFilters: () => void
}

export const FiltrosAvancados = ({
  isDrawerOpen,
  setIsDrawerOpen,
  localFilters,
  updateLocalFilter,
  applyFilters,
  clearFilters,
}: FiltrosAvancadosProps) => {
  const { categorias, isLoading: loadingCategorias } = usePremioCategoria()

  return (
    <div className='flex justify-center'>
      <Sheet open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <SheetTrigger className='w-full'>
          <Button className='flex w-full items-center gap-2 rounded-xl border border-[#262626] bg-esforce-black-300/70 px-6 py-3'>
            <FiFilter size={20} />
            Filtros Avançados
            {Object.values(localFilters).some(
              (value) =>
                value !== '' &&
                value !== false &&
                value !== 'nome' &&
                value !== 'asc',
            ) && (
              <span className='ml-1 rounded-full bg-esforce-black-200 px-2 py-1 text-xs text-esforce-green-200'>
                {
                  Object.values(localFilters).filter(
                    (value) =>
                      value !== '' &&
                      value !== false &&
                      value !== 'nome' &&
                      value !== 'asc',
                  ).length
                }
              </span>
            )}
          </Button>
        </SheetTrigger>
        <SheetContent
          side='left'
          className='max-h-[100vh] w-full max-w-full overflow-y-auto'
        >
          <SheetHeader>
            <SheetTitle>Filtros</SheetTitle>
            <SheetDescription>
              Filtre os prêmios de acordo com suas preferências.
            </SheetDescription>
          </SheetHeader>
          <form className='space-y-8 py-6'>
            {/* Categoria */}
            <div className='space-y-4'>
              <h3 className='text-lg font-semibold text-esforce-white-100'>
                Categoria
              </h3>
              <Select
                value={localFilters.categoria}
                onValueChange={(value) => updateLocalFilter('categoria', value)}
              >
                <SelectTrigger className='h-12 w-full rounded-xl border-none bg-esforce-black-350 text-sm text-esforce-white-100'>
                  <SelectValue
                    placeholder={
                      loadingCategorias
                        ? 'Carregando...'
                        : 'Selecione uma categoria'
                    }
                  />
                </SelectTrigger>
                <SelectContent className='border-none bg-esforce-black-350 text-sm text-esforce-white-100'>
                  <SelectItem value='todos'>Todas as categorias</SelectItem>
                  {categorias?.map((categoria) => (
                    <SelectItem key={categoria.id} value={categoria.id}>
                      {categoria.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Faixa de preço */}
            <div className='space-y-4'>
              <h3 className='text-lg font-semibold text-esforce-white-100'>
                Faixa de preço
              </h3>
              <div className='space-y-4'>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <label className='text-sm text-esforce-white-200'>De</label>
                    <Input
                      value={localFilters.valorMinimo}
                      onChange={(e) =>
                        updateLocalFilter('valorMinimo', e.target.value)
                      }
                      placeholder='100'
                      type='number'
                      className='border-esforce-black-400 h-12 w-full rounded-xl border bg-transparent px-4 text-esforce-white-100 placeholder:text-esforce-white-200 focus:border-esforce-green-200 focus:outline-none'
                    />
                  </div>
                  <div className='space-y-2'>
                    <label className='text-sm text-esforce-white-200'>
                      até
                    </label>
                    <Input
                      value={localFilters.valorMaximo}
                      onChange={(e) =>
                        updateLocalFilter('valorMaximo', e.target.value)
                      }
                      placeholder='500'
                      type='number'
                      className='border-esforce-black-400 h-12 w-full rounded-xl border bg-transparent px-4 text-esforce-white-100 placeholder:text-esforce-white-200 focus:border-esforce-green-200 focus:outline-none'
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Premiação por ranking */}
            <div className='space-y-4'>
              <h3 className='text-lg font-semibold text-esforce-white-100'>
                Premiação por ranking
              </h3>
              <div className='space-y-3'>
                <div className='flex items-center space-x-3'>
                  <input
                    type='checkbox'
                    id='ranking-geral'
                    checked={localFilters.rankingGeral}
                    onChange={(e) =>
                      updateLocalFilter('rankingGeral', e.target.checked)
                    }
                    className='h-4 w-4 rounded border-esforce-black-300 bg-transparent text-esforce-green-200 focus:ring-esforce-green-200'
                  />
                  <div className='flex items-center space-x-2'>
                    <span className='text-lg text-yellow-400'>🏆</span>
                    <label
                      htmlFor='ranking-geral'
                      className='cursor-pointer text-sm text-esforce-white-100'
                    >
                      Ranking Geral
                    </label>
                  </div>
                </div>

                <div className='flex items-center space-x-3'>
                  <input
                    type='checkbox'
                    id='ranking-cidade'
                    checked={localFilters.rankingCidade}
                    onChange={(e) =>
                      updateLocalFilter('rankingCidade', e.target.checked)
                    }
                    className='h-4 w-4 rounded border-esforce-black-300 bg-transparent text-esforce-green-200 focus:ring-esforce-green-200'
                  />
                  <div className='flex items-center space-x-2'>
                    <span className='text-lg text-blue-400'>📍</span>
                    <label
                      htmlFor='ranking-cidade'
                      className='cursor-pointer text-sm text-esforce-white-100'
                    >
                      Ranking por Cidade
                    </label>
                  </div>
                </div>

                <div className='flex items-center space-x-3'>
                  <input
                    type='checkbox'
                    id='ranking-escola'
                    checked={localFilters.rankingEscola}
                    onChange={(e) =>
                      updateLocalFilter('rankingEscola', e.target.checked)
                    }
                    className='h-4 w-4 rounded border-esforce-black-300 bg-transparent text-esforce-green-200 focus:ring-esforce-green-200'
                  />
                  <div className='flex items-center space-x-2'>
                    <span className='text-lg text-green-400'>🏫</span>
                    <label
                      htmlFor='ranking-escola'
                      className='cursor-pointer text-sm text-esforce-white-100'
                    >
                      Ranking por Escola
                    </label>
                  </div>
                </div>

                <div className='flex items-center space-x-3'>
                  <input
                    type='checkbox'
                    id='ranking-turma'
                    checked={localFilters.rankingTurma}
                    onChange={(e) =>
                      updateLocalFilter('rankingTurma', e.target.checked)
                    }
                    className='h-4 w-4 rounded border-esforce-black-300 bg-transparent text-esforce-green-200 focus:ring-esforce-green-200'
                  />
                  <div className='flex items-center space-x-2'>
                    <span className='text-lg text-purple-400'>👥</span>
                    <label
                      htmlFor='ranking-turma'
                      className='cursor-pointer text-sm text-esforce-white-100'
                    >
                      Ranking por Turma
                    </label>
                  </div>
                </div>

                <div className='flex items-center space-x-3'>
                  <input
                    type='checkbox'
                    id='nao-vinculado'
                    checked={localFilters.naoVinculado}
                    onChange={(e) =>
                      updateLocalFilter('naoVinculado', e.target.checked)
                    }
                    className='h-4 w-4 rounded border-esforce-black-300 bg-transparent text-esforce-green-200 focus:ring-esforce-green-200'
                  />
                  <div className='flex items-center space-x-2'>
                    <span className='text-lg text-gray-400'>🏅</span>
                    <label
                      htmlFor='nao-vinculado'
                      className='cursor-pointer text-sm text-esforce-white-100'
                    >
                      Não vinculado a ranking
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Posições premiadas */}
            <div className='space-y-4'>
              <h3 className='text-lg font-semibold text-esforce-white-100'>
                Posições premiadas
              </h3>
              <div className='grid grid-cols-2 gap-4'>
                <div className='space-y-2'>
                  <label className='text-sm text-esforce-white-200'>De</label>
                  <Input
                    value={localFilters.posicaoMinima}
                    onChange={(e) =>
                      updateLocalFilter('posicaoMinima', e.target.value)
                    }
                    placeholder='1'
                    type='number'
                    className='border-esforce-black-400 h-12 w-full rounded-xl border bg-transparent px-4 text-esforce-white-100 placeholder:text-esforce-white-200 focus:border-esforce-green-200 focus:outline-none'
                  />
                </div>
                <div className='space-y-2'>
                  <label className='text-sm text-esforce-white-200'>até</label>
                  <Input
                    value={localFilters.posicaoMinima}
                    onChange={(e) =>
                      updateLocalFilter('posicaoMaxima', e.target.value)
                    }
                    placeholder='10'
                    type='number'
                    className='border-esforce-black-400 h-12 w-full rounded-xl border bg-transparent px-4 text-esforce-white-100 placeholder:text-esforce-white-200 focus:border-esforce-green-200 focus:outline-none'
                  />
                </div>
              </div>
            </div>

            {/* Botões de ação */}
            <div className='flex justify-end space-x-3 pt-6'>
              <Button
                type='button'
                onClick={clearFilters}
                variant='outline'
                className='border-esforce-black-300 bg-esforce-black-300 text-esforce-white-100 hover:bg-esforce-black-300'
              >
                Limpar Filtros
              </Button>
              <Button
                type='button'
                onClick={applyFilters}
                className='bg-esforce-green-200 text-esforce-black-200 hover:bg-esforce-green-100'
              >
                Aplicar Filtros
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  )
}
